export default class Lighting {
  /**
   * @param {ThreeScene} sceneInstance – main ThreeScene instance (contains modelScale)
   * @param {THREE.WebGLRenderer} renderer
   * @param {THREE.Scene} scene
   */
  constructor(sceneInstance, renderer, scene) {
    this.sceneInstance = sceneInstance;
    this.renderer      = renderer;
    this.scene         = scene;

    // Ceiling spot parameters (in inches)
    this.CEILING_HEIGHT_INCHES      = 150; // height of ceiling above floor
    this.CEILING_SPOT_OFFSET_INCHES = -50;  // how far back from cabinet front
    this.CEILING_SPOT_INTENCITY     = 3.5;

    // Minimum distance between spot lights (in inches) to avoid overlap
    this.SPOT_MIN_DISTANCE_INCHES = 20;

    // Note: lower cabinet lights omitted for brevity
  }

  /**
   * Compute raw spotlight positions from front-line segments.
   *
   * @param {Array<{from:{x,y}, to:{x,y}, shapeCenter?:{x,y}}>} lines
   * @param {number} offsetInches – offset inward from each line (in inches)
   * @param {number} heightInches – vertical placement of spot (in inches)
   * @returns {Array<{x:number, y:number, z:number}>}
   */
  getSpotPositionsFromFrontLines(lines, offsetInches, heightInches) {
    return lines.map(line => {
      // 1. Edge vector and length
      const dx = line.to.x - line.from.x;
      const dy = line.to.y - line.from.y;
      const len = Math.hypot(dx, dy) || 1;

      // 2. Inward normal (assumes clockwise winding): (-dy, dx) normalized
      let nx = -dy / len;
      let nz =  dx / len;

      // 3. Determine correct normal direction using shapeCenter if provided
      const midX = (line.from.x + line.to.x) / 2;
      const midY = (line.from.y + line.to.y) / 2;
      const center = line.shapeCenter || { x: midX, y: midY };
      const dot = (center.x - midX) * nx + (center.y - midY) * nz;
      if (dot < 0) {
        nx = -nx;
        nz = -nz;
      }

      // 4. Offset mid-point along inward normal
      return {
        x: midX + nx * offsetInches,
        y: heightInches,
        z: midY + nz * offsetInches
      };
    });
  }

  /**
   * Filter out points that are closer than minDist inches to any already kept point.
   *
   * @param {Array<{x:number, y:number, z?:number}>} points
   * @param {number} minDist – minimum allowed distance between points (in inches)
   * @returns {Array<{x,y,z}>}
   */
  filterUniqueSpots(points, minDist) {
    const unique = [];
    points.forEach(pt => {
      const isFar = unique.every(u => {
        // compare only XZ distance if Z undefined in some points
        const dx = pt.x - u.x;
        const dz = (pt.z || 0) - (u.z || 0);
        return Math.hypot(dx, dz) > minDist;
      });
      if (isFar) {
        unique.push(pt);
      }
    });
    return unique;
  }

  /**
   * Given an array of points {x, y, z}, returns a Set of indices corresponding to:
   *  - the four corners (upper-left, upper-right, lower-left, lower-right)
   *  - one center point
   */
  computeShadowIndices(points) {
    // 1. Compute bounding‐box extents in XZ
    const xs = points.map(p => p.x);
    const zs = points.map(p => p.z);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minZ = Math.min(...zs), maxZ = Math.max(...zs);

    // 2. Find the index closest to each corner
    const corners = [
      { x: minX, z: maxZ },  // upper-left
      { x: maxX, z: maxZ },  // upper-right
      { x: minX, z: minZ },  // lower-left
      { x: maxX, z: minZ }   // lower-right
    ];
    const cornerIndices = corners.map(c => {
      let bestIdx = 0, bestDist = Infinity;
      points.forEach((pt, i) => {
        const dx = pt.x - c.x, dz = pt.z - c.z;
        const d2 = dx*dx + dz*dz;
        if (d2 < bestDist) {
          bestDist = d2;
          bestIdx  = i;
        }
      });
      return bestIdx;
    });

    // 3. Compute the average (center) point and find nearest index
    const avg = points.reduce((acc, pt) => {
      acc.x += pt.x; acc.z += pt.z;
      return acc;
    }, { x: 0, z: 0 });
    avg.x /= points.length;
    avg.z /= points.length;

    let centerIdx = 0, minCenterDist = Infinity;
    points.forEach((pt, i) => {
      const dx = pt.x - avg.x, dz = pt.z - avg.z;
      const d2 = dx*dx + dz*dz;
      if (d2 < minCenterDist) {
        minCenterDist  = d2;
        centerIdx      = i;
      }
    });

    // 4. Merge corner + center indices into a Set
    return new Set([...cornerIndices, centerIdx]);
  }

  /**
   * Add ceiling spotlights above upper cabinets.
   *
   * @param {Array<{from:{x,y}, to:{x,y}, shapeCenter?:{x,y}}>} frontLinesUpper
   */
  addCabinetLights(frontLinesUpper) {
    const { THREE } = window;
    const scale     = this.sceneInstance.modelScale;

    // 1. Generate raw spotlight positions (in inches)
    const rawPositions = this.getSpotPositionsFromFrontLines(
      frontLinesUpper,
      this.CEILING_SPOT_OFFSET_INCHES,
      this.CEILING_HEIGHT_INCHES
    );

    // 2. Filter out positions that are too close
    const filtered = this.filterUniqueSpots(
      rawPositions,
      this.SPOT_MIN_DISTANCE_INCHES
    );

    // 3. Compute which indices should cast shadows
    const shadowIndices = this.computeShadowIndices(filtered);
    //console.log("shadowIndices", shadowIndices);
    //console.log("filtered", filtered);

    // 4. Create SpotLights
    filtered.forEach((pt, idx) => {
      const posX = pt.x * scale;
      const posY = pt.y * scale;
      const posZ = pt.z * scale;

      const spot = new THREE.SpotLight(0xffffff, this.CEILING_SPOT_INTENCITY);
      spot.position.set(posX, posY, posZ);

      // Aim straight down at floor level (Y = 0)
      spot.target.position.set(posX, 0, posZ);
      spot.lookAt(new THREE.Vector3(posX, 0, posZ));

      spot.angle    = Math.PI / Math.PI;
      spot.penumbra = 0.3;
      spot.decay    = 0.5;
      spot.distance = 200 * scale;

      // only corner+center spots cast shadows
      spot.castShadow = shadowIndices.has(idx);

      spot.shadow.mapSize.set(1024, 1024);
      spot.shadow.bias       = -0.0005;
      spot.shadow.normalBias = 0.05;
      spot.shadow.radius     = 4;

      this.scene.add(spot);
      this.scene.add(spot.target);
    });

    // const hemi = new window.THREE.HemisphereLight(0xffffff, 0x444444, 0.6);
    // this.scene.add(hemi);
  }
}
